import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// components
import ChatBubble from 'eCoach/ChatBubble';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';

// styles
import * as Buttons from 'theme/components/Buttons.style';
import * as StyledPromptControls from 'eCoach/_styles/PromptControls.style';
import * as Text from 'theme/components/Text.style';

interface DateReason {
  date: string;
  reason: string;
}

interface WarningLetterReasonInputProps {
  sendUserInput: (text: string, type: number) => void;
  inputType: number;
  steps?: Array<any>;
}

export default function WarningLetterReasonInput({
  sendUserInput,
  inputType,
  steps = [],
}: WarningLetterReasonInputProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const [dateReasons, setDateReasons] = useState<DateReason[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Extract dates from the last message
  useEffect(() => {
    if (steps && steps.length > 0) {
      const lastStep = steps[steps.length - 1];
      if (lastStep?.outputs && lastStep.outputs.length > 0) {
        const lastOutput = lastStep.outputs[lastStep.outputs.length - 1];
        if (lastOutput?.message) {
          // Extract the dates
          // English pattern: "following dates: [dates]:"
          // French pattern: "dates suivantes : [dates] :"
          const [, englishDates] =
            lastOutput.message.match(/following dates: ([^:]+):/) || [];
          const [, frenchDates] =
            lastOutput.message.match(/dates suivantes\s*:\s*([^:]+):/) || [];

          const matchedDates = englishDates || frenchDates;

          if (matchedDates) {
            const dates = matchedDates.split(',').map((date) => date.trim());

            // Initialize the date-reason pairs
            const initialDateReasons = dates.map((date) => ({
              date,
              reason: '',
            }));
            setDateReasons(initialDateReasons);
          }
        }
      }
    }
  }, [steps]);

  const handleReasonChange = (index: number, value: string) => {
    const updatedReasons = [...dateReasons];
    updatedReasons[index].reason = value;
    setDateReasons(updatedReasons);
    setErrorMessage('');
  };

  const areAllReasonsProvided = useCallback(
    () => dateReasons.every((item) => item.reason.trim() !== ''),
    [dateReasons]
  );

  const handleSubmit = useCallback(() => {
    if (!areAllReasonsProvided()) {
      setErrorMessage(t('Please provide a reason for each date'));
      return;
    }

    // Format reasons as "Reason1, Reason2, ..."
    const formattedReasons = dateReasons.map((item) => item.reason).join(', ');

    sendUserInput(formattedReasons, inputType);
  }, [dateReasons, inputType, sendUserInput, t, areAllReasonsProvided]);

  return (
    <ChatBubble sentByUser>
      <StyledPromptControls.InputGroup>
        <Text.P2>
          {t('Please provide reasons for the following absences:')}
        </Text.P2>

        {dateReasons.map((item, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
              {item.date}
            </Typography>
            <FormControl fullWidth>
              <TextField
                label={t('Reason for absence')}
                variant="outlined"
                value={item.reason}
                onChange={(e) => handleReasonChange(index, e.target.value)}
                placeholder={t('e.g., Personal, Sick, Family emergency')}
                fullWidth
              />
            </FormControl>
          </Paper>
        ))}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Buttons.SecondaryDark
          onClick={handleSubmit}
          inactive={!areAllReasonsProvided()}
          small
          stretch
        >
          {t('Submit')}
        </Buttons.SecondaryDark>
      </StyledPromptControls.InputGroup>
    </ChatBubble>
  );
}
